import { useMemo } from "react";
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useData } from "../../contexts/DataContext";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, CalendarIcon, ListChecks } from "lucide-react";
import { Avatar, STATUS_META, PRIORITY_META, isOverdue } from "../ui-bits";
import { cn } from "../../lib/utils";

function Card({ task, users, onOpen }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id });
  const { tasks } = useData();
  const subs = tasks.filter((t) => t.parent_id === task.id);
  const doneSubs = subs.filter((s) => s.status === "done").length;
  const pm = PRIORITY_META[task.priority];
  const overdue = isOverdue(task);

  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
      data-testid={`kanban-card-${task.id}`}
      onClick={() => onOpen(task)}
      style={transform ? { transform: `translate(${transform.x}px, ${transform.y}px)` } : undefined}
      className={cn("bg-card border border-border rounded-md p-2.5 mb-2 cursor-grab active:cursor-grabbing hover:border-[#E31E24]/50 transition-colors shadow-sm",
        isDragging && "opacity-40 z-50 relative")}>
      <div className="flex items-start justify-between gap-2">
        <p className={cn("text-sm font-medium leading-snug", task.status === "done" && "line-through text-muted-foreground")}>{task.title}</p>
        <span className="h-2 w-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: pm?.color }} title={pm?.label} />
      </div>
      <div className="flex items-center gap-2 mt-2">
        {task.due_date && (
          <span className={cn("flex items-center gap-1 text-[11px]", overdue ? "text-[#E31E24] font-semibold" : "text-muted-foreground")}>
            <CalendarIcon size={11} />{task.due_date.slice(5)}
          </span>
        )}
        {subs.length > 0 && (
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <ListChecks size={11} />{doneSubs}/{subs.length}
          </span>
        )}
        <span className="ml-auto flex items-center -space-x-1.5">
          {(task.assignees || []).slice(0, 3).map((id) => {
            const u = users.find((x) => x.id === id);
            return u ? <Avatar key={id} name={u.name} userId={u.id} size="sm" className="ring-2 ring-card" /> : null;
          })}
        </span>
      </div>
    </div>
  );
}

function Column({ status, tasks, users, onOpen, onAdd }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const m = STATUS_META[status];
  return (
    <div ref={setNodeRef} data-testid={`kanban-column-${status}`}
      className={cn("flex-1 min-w-[240px] max-w-[320px] rounded-lg bg-surface border p-2 flex flex-col transition-colors", isOver ? "border-[#E31E24]" : "border-border")}>
      <div className="flex items-center gap-2 px-1.5 py-1.5">
        <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: m.color }} />
        <span className="text-xs font-semibold uppercase tracking-[0.05em]">{m.label}</span>
        <span className="text-xs text-muted-foreground">{tasks.length}</span>
        <button data-testid={`kanban-add-${status}`} onClick={() => onAdd(status)}
          className="ml-auto h-5 w-5 flex items-center justify-center rounded text-muted-foreground hover:text-[#E31E24] hover:bg-[#E31E24]/10 transition-colors">
          <Plus size={14} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-0.5 min-h-[120px]">
        {tasks.map((t) => <Card key={t.id} task={t} users={users} onOpen={onOpen} />)}
      </div>
    </div>
  );
}

export default function KanbanView({ tasks, users, onOpenTask, onAddTask }) {
  const { refreshTasks } = useData();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const byStatus = useMemo(() => {
    const top = tasks.filter((t) => !t.parent_id);
    return Object.keys(STATUS_META).reduce((acc, s) => ({ ...acc, [s]: top.filter((t) => t.status === s) }), {});
  }, [tasks]);

  const onDragEnd = async ({ active, over }) => {
    if (!over) return;
    const task = tasks.find((t) => t.id === active.id);
    if (!task || task.status === over.id) return;
    try {
      await api.post(`/tasks/${task.id}/move`, { status: over.id });
      refreshTasks();
      toast.success(`Moved to ${STATUS_META[over.id].label}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4 h-full" data-testid="kanban-board">
        {Object.keys(STATUS_META).map((s) => (
          <Column key={s} status={s} tasks={byStatus[s]} users={users} onOpen={onOpenTask} onAdd={(status) => onAddTask({ key: status, status, projectId: tasks[0]?.project_id })} />
        ))}
      </div>
    </DndContext>
  );
}
